import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import axios from 'axios';

/**
 * Wholesale Catalog Browser
 *
 * Allows users to browse supplier catalogs and select items
 * for wholesale look configuration
 *
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog callback
 * @param {function} onSelect - Callback when item selected (supplier, catalogItem)
 */
const WholesaleCatalogBrowser = ({ open, onClose, onSelect }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSizes, setSelectedSizes] = useState([]);

  useEffect(() => {
    if (open) {
      fetchSuppliers();
    }
  }, [open]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/wholesale-suppliers?status=active');
      console.log('📦 Fetched suppliers:', response.data.suppliers);
      if (response.data.success) {
        setSuppliers(response.data.suppliers);
        // Auto-select first supplier if only one
        if (response.data.suppliers.length === 1) {
          setSelectedSupplier(response.data.suppliers[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    if (!selectedSupplier) {
      setError('Please select a supplier first');
      return;
    }

    try {
      setLoading(true);
      setCatalog([]);
      setCatalogLoaded(false);

      // Build query params for server-side filtering with pagination
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '100'); // Load 100 styles at a time
      if (searchTerm) params.append('search', searchTerm);
      if (brandFilter) params.append('brand', brandFilter);

      // Use NEW stylination endpoint for proper grouping
      const url = `/api/catalog-styles/${selectedSupplier}?${params.toString()}`;
      console.log('📡 Fetching catalog with stylination:', { searchTerm, brandFilter, url });

      const response = await axios.get(url);
      if (response.data.success) {
        setCatalog(response.data.styles || []);
        setCatalogLoaded(true);
        console.log('✅ Catalog loaded:', response.data.styles.length, 'styles');
        console.log('📊 Pagination:', response.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching catalog:', err);
      setError('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (item) => {
    const supplier = suppliers.find(s => s.id === selectedSupplier);
    if (onSelect && supplier) {
      console.log('✅ Selecting item:', item);
      console.log('✅ Selected color:', selectedColor);
      console.log('✅ Selected sizes:', selectedSizes);

      onSelect({
        supplier: {
          id: supplier.id,
          supplierCode: supplier.supplierCode,
          companyName: supplier.companyName
        },
        catalogItem: {
          id: item._id || item.catalogItemId,
          catalogItemId: item.catalogItemId,
          itemName: item.itemName,
          brand: item.brand,
          category: item.category,
          colors: item.colors || [],
          sizes: item.sizes || [],
          costTiers: item.costTiers || [],
          moq: item.moq,
          leadTimeDays: item.leadTimeDays,
          fabricWeight: item.fabricWeight,
          fabricComposition: item.fabricComposition,
          // Pass selected color and sizes
          selectedColor: selectedColor || item.colors?.[0] || '',
          selectedSizes: selectedSizes.length > 0 ? selectedSizes : item.sizes || [],
          // Pass mockup image and print area data for Garment Designer
          imageUrl: item.imageUrl || null,
          printableArea: item.printableArea || null
        }
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSupplier(null);
    setCatalog([]);
    setSearchTerm('');
    setCategoryFilter('');
    setBrandFilter('');
    setExpandedItem(null);
    setCatalogLoaded(false);
    setError(null);
    setSelectedColor('');
    setSelectedSizes([]);
    onClose();
  };

  // Auto-select first color when expanding an item
  useEffect(() => {
    if (expandedItem) {
      const item = catalog.find(i => i.catalogItemId === expandedItem);
      if (item && item.colors && item.colors.length > 0) {
        setSelectedColor(item.colors[0]);
      }
      if (item && item.sizes) {
        setSelectedSizes(item.sizes);
      }
    } else {
      setSelectedColor('');
      setSelectedSizes([]);
    }
  }, [expandedItem, catalog]);

  // Filter catalog
  const filteredCatalog = catalog.filter(item => {
    const matchesSearch = !searchTerm ||
      item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.catalogItemId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesBrand = !brandFilter || item.brand?.toLowerCase().includes(brandFilter.toLowerCase());

    return matchesSearch && matchesCategory && matchesBrand && !item.discontinued;
  });

  // Get unique categories and brands
  const categories = [...new Set(catalog.map(item => item.category).filter(Boolean))];
  const brands = [...new Set(catalog.map(item => item.brand).filter(Boolean))].sort();

  // Debug logging removed to prevent console flooding

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Browse Wholesale Catalog
        <Typography variant="caption" display="block" color="text.secondary">
          Select a supplier and catalog item for your wholesale look
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Supplier Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Supplier</InputLabel>
              <Select
                value={selectedSupplier || ''}
                onChange={(e) => {
                  console.log('🏪 Supplier selected:', e.target.value);
                  console.log('📊 State before change:', { selectedSupplier, catalogLoaded });
                  setSelectedSupplier(e.target.value);
                  setCatalog([]);
                  setCatalogLoaded(false);
                  console.log('📊 catalogLoaded set to false');
                }}
                label="Supplier"
              >
                {suppliers.map(supplier => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StoreIcon fontSize="small" />
                      {supplier.companyName}
                      <Chip
                        label={`${supplier.catalogItemCount || 0} items`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Warning and Pre-Load Filters */}
          {selectedSupplier && !catalogLoaded && (
            <>
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Large Catalog Warning:</strong> This supplier has {suppliers.find(s => s.id === selectedSupplier)?.catalogItemCount || 0} items.
                  </Typography>
                  <Typography variant="body2">
                    Use the filters below to narrow your search before loading the catalog to prevent performance issues.
                  </Typography>
                </Alert>
              </Grid>

              {/* Pre-load filters */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search by name, SKU, or brand"
                  placeholder="e.g., Bella Canvas, 3001, soft style..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Brand</InputLabel>
                  <Select
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    label="Filter by Brand"
                  >
                    <MenuItem value="">All Brands</MenuItem>
                    <MenuItem value="bella">Bella+Canvas</MenuItem>
                    <MenuItem value="next level">Next Level</MenuItem>
                    <MenuItem value="hanes">Hanes</MenuItem>
                    <MenuItem value="independent trading">Independent Trading Co.</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={fetchCatalog}
                  disabled={loading}
                  size="large"
                  fullWidth
                >
                  {loading ? 'Loading Catalog...' : 'Load Catalog'}
                </Button>
              </Grid>
            </>
          )}

          {/* Filters (shown after catalog loaded) */}
          {catalogLoaded && (
            <>
              {/* Search */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search catalog"
                  placeholder="Search by name, SKU, or brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>

              {/* Brand Filter */}
              {brands.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={brandFilter}
                      onChange={(e) => setBrandFilter(e.target.value)}
                      label="Brand"
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {brands.map(brand => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Category Filter */}
              {categories.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map(category => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Results count */}
              <Grid item xs={12}>
                <Alert severity="info">
                  Showing {filteredCatalog.length} of {catalog.length} items
                </Alert>
              </Grid>
            </>
          )}
        </Grid>

        {/* Catalog Items */}
        {!selectedSupplier ? (
          <Alert severity="info">Select a supplier to browse their catalog</Alert>
        ) : !catalogLoaded && !loading ? (
          <Alert severity="info">Click "Load Catalog" above to view available items</Alert>
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>Loading catalog...</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a moment for large catalogs
            </Typography>
          </Box>
        ) : filteredCatalog.length === 0 ? (
          <Alert severity="info">
            No items found matching your filters. Try adjusting your search or filters.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Style</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Colors</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sizes</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Stock</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCatalog.map((item) => (
                  <TableRow key={item.catalogItemId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.itemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        SKU: {item.catalogItemId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.brand}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.colors?.length || 0} colors
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {item.sizes?.slice(0, 4).map(size => (
                          <Chip key={size} label={size} size="small" variant="outlined" />
                        ))}
                        {item.sizes?.length > 4 && (
                          <Chip label={`+${item.sizes.length - 4}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.costTiers && item.costTiers.length > 0 ? (
                        <Typography variant="body2">
                          ${item.costTiers[0].unitCost.toFixed(2)}
                          {item.costTiers.length > 1 && (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {' '}(+{item.costTiers.length - 1} tiers)
                            </Typography>
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.lastInventorySync ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={item.inStock ? 'In Stock' : 'Out of Stock'}
                            size="small"
                            color={item.inStock ? 'success' : 'error'}
                            variant="outlined"
                          />
                          {item.totalAvailableQty !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              {item.totalAvailableQty.toLocaleString()} units
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Chip label="Unknown" size="small" variant="outlined" color="default" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => setExpandedItem(expandedItem === item.catalogItemId ? null : item.catalogItemId)}
                      >
                        {expandedItem === item.catalogItemId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Expanded Item Details Dialog */}
        {expandedItem && filteredCatalog.find(item => item.catalogItemId === expandedItem) && (
          <Dialog
            open={!!expandedItem}
            onClose={() => setExpandedItem(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {filteredCatalog.find(item => item.catalogItemId === expandedItem)?.itemName}
            </DialogTitle>
            <DialogContent>
              {(() => {
                const item = filteredCatalog.find(i => i.catalogItemId === expandedItem);
                return (
                  <Grid container spacing={2}>
                    {/* Quick Info */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {item.fabricWeight && (
                          <Chip label={item.fabricWeight} size="small" variant="outlined" />
                        )}
                        {item.fabricComposition && (
                          <Chip label={item.fabricComposition} size="small" variant="outlined" />
                        )}
                        {item.moq > 1 && (
                          <Chip label={`MOQ: ${item.moq}`} size="small" color="warning" variant="outlined" />
                        )}
                        {item.leadTimeDays && (
                          <Chip label={`${item.leadTimeDays} days lead time`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Grid>

                    {/* Colors */}
                    {item.colors && item.colors.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Select Color ({item.colors.length} available):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {item.colors.map(color => (
                            <Chip
                              key={color}
                              label={color}
                              size="small"
                              onClick={() => setSelectedColor(color)}
                              color={selectedColor === color ? 'primary' : 'default'}
                              variant={selectedColor === color ? 'filled' : 'outlined'}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                        {selectedColor && (
                          <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                            ✓ Selected: {selectedColor}
                          </Typography>
                        )}
                      </Grid>
                    )}

                    {/* Sizes */}
                    {item.sizes && item.sizes.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Available Sizes:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {item.sizes.map(size => (
                            <Chip key={size} label={size} size="small" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Price Tiers */}
                    {item.costTiers && item.costTiers.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Pricing Breakdown:
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Unit Cost</TableCell>
                                <TableCell>Tier</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.costTiers.map((tier, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    {tier.minQuantity}
                                    {tier.maxQuantity ? `-${tier.maxQuantity}` : '+'}
                                  </TableCell>
                                  <TableCell>${tier.unitCost.toFixed(2)}</TableCell>
                                  <TableCell>{tier.tierLabel || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}

                    {/* Warehouse Inventory */}
                    {item.inventoryByWarehouse && item.inventoryByWarehouse.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Warehouse Inventory:
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Warehouse</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell>Last Updated</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.inventoryByWarehouse.map((wh, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Chip
                                      label={wh.warehouseCode}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      color={wh.quantity > 0 ? 'success.main' : 'error.main'}
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {wh.quantity.toLocaleString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                      {wh.lastUpdated ? new Date(wh.lastUpdated).toLocaleDateString() : 'N/A'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {item.totalAvailableQty !== undefined && (
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {item.totalAvailableQty.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={item.inStock ? 'In Stock' : 'Out of Stock'}
                                      size="small"
                                      color={item.inStock ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}

                    {/* Inventory Sync Status */}
                    {item.lastInventorySync && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Last inventory sync: {new Date(item.lastInventorySync).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpandedItem(null)}>Cancel</Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  const item = filteredCatalog.find(i => i.catalogItemId === expandedItem);
                  if (item) handleSelectItem(item);
                }}
              >
                Select This Item
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WholesaleCatalogBrowser;
